import "./i18n/config";
import "./index.css";
import "driver.js/dist/driver.css";
import "@/styles/driverjs.css";
import { Auth0Provider } from "@auth0/auth0-react";
import { initSegment, type SegmentSource } from "@pepperstone/analytics-web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App, router } from "./App";
import { ErrorComponent } from "./components/Error";
import { UnauthenticatedHeader } from "./components/Header";
import { InactivityMonitor } from "./components/InactivityMonitor";
import { getAuth0Domain, getUrl } from "./utils/domainResolver";
import { handleAuth0Redirect } from "./utils/handleAuth0Redirect";
import { DatadogErrorBoundary } from "./utils/logger";

initSegment({
  source: import.meta.env.VITE_SEGMENT_SOURCE as SegmentSource,
  callPageOnInit: false,
});

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 10000,
    },
  },
});

const rootElement = document.getElementById("app")!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <DatadogErrorBoundary
        fallback={
          <>
            <UnauthenticatedHeader />
            <ErrorComponent />
          </>
        }
      >
        <Auth0Provider
          domain={getAuth0Domain()}
          clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
          onRedirectCallback={(appState) =>
            handleAuth0Redirect(appState, (to) => router.history.replace(to))
          }
          authorizationParams={{
            redirect_uri: window.location.origin,
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: import.meta.env.VITE_AUTH0_SCOPE,
            sca_origin: getUrl(import.meta.env.VITE_AUTH0_SCA_ORIGIN),
          }}
        >
          <QueryClientProvider client={client}>
            <App />
            <InactivityMonitor
              inactivityDuration={import.meta.env.VITE_INACTIVITY_DURATION}
            />
          </QueryClientProvider>
        </Auth0Provider>
      </DatadogErrorBoundary>
    </StrictMode>,
  );
}
